import React from 'react';
import { Skeleton } from '@mui/material';

/* Skeleton placeholders for the Manage / Credit invoice page.
   Reuses the real layout class names so the shimmer lines up with the
   actual KPI cards and table columns. */

export const KpiCardsSkeleton: React.FC = () => (
    // Two skeleton cards to match the two real KPIs the page renders
    // (Total Invoiced + Paid). The previous `[0, 1, 2]` array was left over
    // from when an Outstanding card existed; without this fix the load
    // state briefly shows three shimmering cards then snaps to two.
    <div className="manage-invoice-v2__kpis">
        {[0, 1].map((i) => (
            <div className="kpi-card" key={i}>
                <div className="kpi-card__top">
                    <Skeleton variant="text" width={90} height={14} animation="wave" />
                    <Skeleton variant="circular" width={36} height={36} animation="wave" />
                </div>
                <Skeleton variant="text" width="70%" height={34} animation="wave" sx={{ mt: 1 }} />
                {i === 0 ? (
                    <Skeleton variant="rounded" width="100%" height={8} animation="wave" sx={{ mt: 2, borderRadius: 999 }} />
                ) : (
                    <Skeleton variant="text" width="45%" height={14} animation="wave" sx={{ mt: 1 }} />
                )}
            </div>
        ))}
    </div>
);

interface InvoiceTableSkeletonProps {
    columns: number;
    rows?: number;
    filters?: 'full' | 'search';
}

export const InvoiceTableSkeleton: React.FC<InvoiceTableSkeletonProps> = ({
    columns,
    rows = 6,
    filters = 'full',
}) => (
    <>
        <div className="invoice-table-header">
            <Skeleton variant="text" width={150} height={26} animation="wave" />
            <div className="invoice-table__filter-bar">
                <Skeleton variant="rounded" width={300} height={40} animation="wave" sx={{ maxWidth: '100%' }} />
                {filters === 'full' && (
                    <>
                        <Skeleton variant="rounded" width={120} height={40} animation="wave" />
                        <Skeleton variant="rounded" width={120} height={40} animation="wave" />
                    </>
                )}
            </div>
        </div>

        <div className="invoice-table-content">
            <div className="invoice-table-scroll-wrapper">
                <div className="invoice-table">
                    <div className="invoice-table__header">
                        {Array.from({ length: columns }).map((_, i) => (
                            <div className="invoice-table__column" key={i}>
                                <Skeleton variant="text" width={56} height={14} animation="wave" />
                            </div>
                        ))}
                    </div>

                    <div className="invoice-table__body">
                        {Array.from({ length: rows }).map((_, r) => (
                            <div className="invoice-table__desktop-row" key={r}>
                                {Array.from({ length: columns }).map((_, c) => (
                                    <div className="invoice-table__desktop-cell" key={c}>
                                        {c === 1 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                                                <Skeleton variant="circular" width={34} height={34} animation="wave" />
                                                <div style={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" height={14} animation="wave" />
                                                    <Skeleton variant="text" width="50%" height={12} animation="wave" />
                                                </div>
                                            </div>
                                        ) : (
                                            <Skeleton variant="text" width="72%" height={16} animation="wave" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </>
);
